import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { SchemaGenerator } from '../../components/SchemaGenerator';
import { Footer } from '../../components/Footer';
import { MainNavigation } from '../../components/MainNavigation';
import { GridBackground } from '../../components/GridBackground';
import { CTA1, CTA2 } from '../../components/CTABanners';
import { AISnippetBlockFeatured } from '../../components/AISnippetBlock';
import { PeopleAlsoAsk } from '../../components/PeopleAlsoAsk';
import { BreadcrumbNav } from '../../components/BreadcrumbNav';
import { PillarContentRenderer } from '../../components/PillarContentRenderer';
import { ArticleFooter } from '../../components/ArticleFooter';
import { loadAllArticles } from '@/services/articleLoader';
import { iptVsCable2026 } from '../../data/pillarPages/IPTVvsCable2026';

const PILLAR_SLUG = 'iptv-vs-cable-2026';

export const IPTVvsCable2026Page = () => {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const articles = await loadAllArticles();
        const found = articles.find(a => a.slug === PILLAR_SLUG);
        if (mounted) {
          setArticle(found || null);
          setLoading(false);
        }
      } catch (e) {
        console.warn('Failed to load pillar article from /articles:', e);
        if (mounted) {
          setArticle(null);
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        Loading article...
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const schemas = [
    {
      type: 'Article',
      data: {
        headline: article.title,
        description: article.excerpt,
        image: 'https://xtreamgate.com/images/iptv-vs-cable-2026-comparison.jpg',
        author: iptVsCable2026.schema.article.author,
        datePublished: article.date,
        dateModified: iptVsCable2026.lastUpdated || article.date
      }
    },
    {
      type: 'FAQ',
      data: {
        faqs: (article.faqItems && article.faqItems.length ? article.faqItems : iptVsCable2026.faqs).map(faq => ({
          question: faq.question,
          answer: faq.answer
        }))
      }
    }
  ];

  return (
    <>
      <Helmet>
        <title>{article.title}</title>
        <meta name="description" content={article.excerpt} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta name="keywords" content={(article.seoKeywords || []).join(', ')} />
        <link rel="canonical" href={`https://xtreamgate.com/blog/${article.slug}`} />
      </Helmet>

      {schemas.map((schema, idx) => (
        <SchemaGenerator key={idx} type={schema.type} data={schema.data} />
      ))}

      <div className="min-h-screen relative">
        <GridBackground />
        <MainNavigation />

        <motion.div className="px-4 py-8 relative z-10" variants={containerVariants} initial="hidden" animate="visible">
          <div className="max-w-4xl mx-auto">
            <BreadcrumbNav items={iptVsCable2026.breadcrumbs} />

            <motion.div variants={itemVariants} className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block px-3 py-1 bg-primary/20 border border-primary/30 text-primary text-sm font-semibold rounded-full">{article.category}</span>
                <span className="text-slate-400 text-sm">{article.readTime}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">{article.title}</h1>
              <p className="text-xl text-slate-300">{article.excerpt}</p>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-8">
              <AISnippetBlockFeatured quickAnswer={article.quickAnswer} keyPoints={article.keyPoints} title="Quick Answer" />
            </motion.div>

            <motion.div variants={itemVariants} className="mb-12 p-6 rounded-lg bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 backdrop-blur-sm">
              <h2 className="text-2xl font-bold text-white mb-4">Why Cable is Losing the Competition</h2>
              <PillarContentRenderer content={iptVsCable2026.expandedAnswer} />
            </motion.div>

            <motion.div variants={itemVariants} className="mb-12 space-y-8">
              {Object.entries(iptVsCable2026.mainContent).map(([key, section], idx) => (
                <div key={idx} className="rounded-lg overflow-hidden bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">{section.heading}</h2>
                  <PillarContentRenderer content={section.content} />
                </div>
              ))}
            </motion.div>

            <motion.div variants={itemVariants} className="mb-12">
              <CTA2
                headline={iptVsCable2026.cta.primary.text}
                subtext={iptVsCable2026.cta.primary.subtext}
                buttonText={iptVsCable2026.cta.primary.buttonText}
                buttonUrl={iptVsCable2026.cta.primary.buttonUrl}
              />
            </motion.div>

            <motion.div variants={itemVariants} className="mb-12">
              <PeopleAlsoAsk
                faqItems={article.faqItems && article.faqItems.length ? article.faqItems : iptVsCable2026.faqs}
              />
            </motion.div>

            <motion.div variants={itemVariants} className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Related Guides</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {iptVsCable2026.internalLinks.map((link, idx) => (
                  <a key={idx} href={link.url} className="p-4 rounded-lg bg-slate-800 border border-slate-700 hover:border-blue-500 transition-all group">
                    <h3 className="font-bold text-white group-hover:text-blue-400 transition-colors">{link.text}</h3>
                    <p className="text-slate-400 text-sm mt-2">{link.context}</p>
                  </a>
                ))}
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="mb-12">
              <CTA1
                headline={iptVsCable2026.cta.secondary.text}
                subtext={iptVsCable2026.cta.secondary.subtext}
                buttonText={iptVsCable2026.cta.secondary.buttonText}
                buttonUrl={iptVsCable2026.cta.secondary.buttonUrl}
              />
            </motion.div>

            {/* Article Footer - Share & Navigation */}
            <ArticleFooter
              title={iptVsCable2026.title}
              nextArticleTitle="Best IPTV USA 2026"
              nextArticleUrl="/blog/best-iptv-usa-2026"
            />
          </div>
        </motion.div>
      </div>

      <Footer />
    </>
  );
};

export default IPTVvsCable2026Page;
